import React, { createContext, useContext, useState, useCallback } from 'react';
import { projectService } from '../services/projectService';
import { projectSharingService } from '../services/projectSharingService';

function normalizeProject(p) {
  if (!p) return null;
  return {
    ...p,
    key: p.jira_project_key,
    ticketCount: p.ticket_count ?? 0,
    week_start_day: p.week_start_day ?? 1,
    // Sharing fields (defaults preserve old behaviour for anything not yet migrated)
    is_owner: p.is_owner ?? true,
    is_shared: p.is_shared ?? false,
    owner_id: p.owner_id ?? null,
  };
}

const ProjectsContext = createContext(null);

export function ProjectsProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [incomingCount, setIncomingCount] = useState(0);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectService.getProjects();
      setProjects((data ?? []).map((p) => normalizeProject(p)));
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh the count of invitations awaiting the current user's action.
  // Cheap call; cached by the server logic. Called by Navbar on mount and after
  // accept/reject/cancel actions.
  const refreshIncomingCount = useCallback(async () => {
    try {
      const data = await projectSharingService.getIncoming();
      setIncomingCount(Array.isArray(data) ? data.length : 0);
    } catch {
      // Don't surface errors here — badge silently shows 0 on failure.
    }
  }, []);

  const getProjectById = useCallback(
    (id) => {
      if (id == null) return undefined;
      return projects.find((p) => String(p.id) === String(id));
    },
    [projects]
  );

  const value = {
    projects,
    setProjects,
    loading,
    fetchProjects,
    getProjectById,
    incomingCount,
    refreshIncomingCount,
  };

  return (
    <ProjectsContext.Provider value={value}>
      {children}
    </ProjectsContext.Provider>
  );
}

export function useProjects() {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
}